import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TermsModal from './TermsModal';
import FaceRegistration from './FaceRegistration';

import  './styles/Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [student, setStudent] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showFaceRegistration, setShowFaceRegistration] = useState(false);
  const navigate = useNavigate();

  const API_BASE_URL = 'https://peacefulsolutionsuniversity.academy/home-aide-api';


  useEffect(() => {
    document.title = "Home Aide Training - Student Login";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
  
    try {
      const response = await axios.post(`${API_BASE_URL}/login.php`, {
        username: username.trim(),
        password: password.trim()
      });

      if (response.data.success) {
        const studentData = response.data.student;
        setStudent(studentData);
        
        if (!studentData.terms_accepted) {
          setShowTerms(true);
        } else if (!studentData.face_id_enabled) {
          setShowFaceRegistration(true);
        } else {
          proceedToDashboard(studentData);
        }
      } else {
        setError(response.data.message || 'Invalid credentials');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTermsAccept = () => {
    setShowTerms(false);
    if (student.face_id_enabled) {
      proceedToDashboard(student);
    } else {
      setShowFaceRegistration(true);
    }
  };

  const handleTermsDecline = () => {
    setShowTerms(false);
    setError('You must accept the terms to use this system');
  };

  const handleFaceRegistrationComplete = async (faceData) => {
    try {
      const response = await axios.post('http://10.0.0.28:80/home-aide-api/register_face.php', {
        studentId: student.id,
        faceData: faceData
      });

      if (response.data.success) {
        proceedToDashboard({
          ...student,
          face_id_enabled: true,
          terms_accepted: true
        });
      } else {
        setError('Failed to register Face ID. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
      console.error('Face registration error:', err);
    } finally {
      setShowFaceRegistration(false);
    }
  };

  const proceedToDashboard = (studentData) => {
    localStorage.setItem('studentToken', studentData.token || 'generated-token');
    localStorage.setItem('studentData', JSON.stringify(studentData));
    navigate('/dashboard');
  };

  return (
    <div className="login-container">
      <div className="login-background"></div>
      
      <div className="login-content">
        <div className="login-header">
          <div className="logo-circle">
            <span>HATC</span>
          </div>
          <h1>Home Aide Training Center LLC</h1>
          <p className="address">1305 S 312th St suite 202, Federal Way, WA 98003</p>
          <p className="phone">(206) 566-4676</p>
        </div>
        
        <div className={`login-box ${isFocused ? 'focused' : ''}`}>
          <div className="login-box-header">
            <h2>Student Portal</h2>
            <p>Sign in to access your training</p>
          </div>
          
          {error && (
            <div className="error-message">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <span>{error}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="username">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                <span>First Name (Username)</span>
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                required
              />
            </div>
            
            <div className="input-group">
              <label htmlFor="password">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                <span>Surname (Password)</span>
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                required
              />
            </div>
            
            <button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <svg className="spinner" viewBox="0 0 50 50">
                    <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                  </svg>
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 7L9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z"/>
                  </svg>
                  <span>Login</span>
                </>
              )}
            </button>
          </form>
          
          <div className="login-footer">
            <p>© {new Date().getFullYear()} Home Aide Training Center LLC</p>
            <p className="help-text">Need help? Contact support at <a href="mailto:support@homeaidetc.com">support@homeaidetc.com</a></p>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      {showTerms && (
        <TermsModal 
          onAccept={handleTermsAccept}
          onDecline={handleTermsDecline}
        />
      )}
      
      {/* Face Registration Component */}
      {showFaceRegistration && student && (
        <FaceRegistration 
          student={student}
          onComplete={handleFaceRegistrationComplete}
        />
      )}
    </div>
  );
};

export default Login;